import {h, Component} from 'preact';
import Button from '@components/common/Button';
import Wallet from '@components/account/Wallet';
import AddressBook from '@components/account/AddressBook';
import AddPaymentForm from '@components/account/Wallet/AddPaymentForm';
import {TextField} from '@components/common/Form/TextField';
import {FormFeedback} from '@components/common/Form';
import Spinner from '@components/common/Spinner';
import {client} from '@core/GraphQLProvider';
import {Query} from 'react-apollo';
import {Formik, Field} from 'formik';
import {GetCustomer} from '@services/Customer/query.graphql';
import User from '@modules/User';
import CustomerService from '../../../services/Customer';
import {$CONTENT_WINDOW} from '@config/selectors';
import prop from 'ramda/src/prop';
import invoke from 'lodash.invoke';
import reject from 'ramda/src/reject';
import not from 'ramda/src/not';
import {trimFormData} from '@util/StringUtils';

const withQuery = ( Component ) => {
    function Wrapper( props ) {
        return (
            <Query client={client}
                   fetchPolicy="network-only"
                   query={GetCustomer}
                   variables={{
                       withCartSummary: true,
                       withAddresses: true,
                       withWallet: true,
                       withAccountDetail: true
                   }}>
                {PaymentSelectionQuery( props )}
            </Query>
        );
    }

    Wrapper.displayName = `withQuery(${Component.displayName || Component.name})`;

    return Wrapper;
};

const ServiceError = () =>
    <div className="text-center">
        <h2 className="my-4 text-lg leading-tight">
            Sorry, we're experiencing a problem and are unable to load your payment options.
        </h2>
        <p className="mb-4 text-grey-dark">
            Please contact our Customer Support team to complete this order by calling
            {' '}
            <b className="text-grey-darker whitespace-no-wrap">{upi.conf.phoneTollFree}</b>
        </p>
    </div>;

export const PaymentSelectionQuery = ( props = {} ) => {
    let retryAttempts = 0;

    return ( {data, loading, error, refetch} ) => {
        if ( loading ) {
            return <Spinner className="justify-center" message="Loading your payment options"/>
        }
        if ( error || !data ) {
            if ( retryAttempts < 2 ) {
                retryAttempts++;
                refetch();
                return <Spinner className="justify-center" message="Still loading your payment options"/>;
            }
            return <ServiceError/>;
        }

        const {wallet, addresses, account, cart} = data.customer;
        const paymentMethods = User.Wallet.normalizeWallet( wallet );

        // note -> the lines below are for future implementation once shipping addresses can be used again
        //  for creating new payment methods
        // const billingAddresses = addresses.filter( addr => addr.addressType === 'Billing' );
        // const shippingAddresses = addresses.filter( addr => addr.addressType === 'Shipping' );
        // const addressBook = User.Address.getDedupedAddressList( billingAddresses, shippingAddresses );
        // note -> for now we can only pass "Billing" addresses
        const addressBook = addresses.filter( addr => addr.addressType === 'Billing' );
        console.log( {addressBook, addresses} );

        return <PaymentSelection
            refetchQuery={refetch}
            loading={loading}
            account={account}
            addressBook={addressBook}
            paymentMethods={paymentMethods}
            poNumber={cart.poNumber || null}
            {...props} />;
    };
};

class PaymentSelection extends Component {

    defaultProps = {
        addressBook: [],
        paymentMethods: []
    };

    constructor( props ) {
        super( props );
        this.onPaymentChange = this.onPaymentChange.bind( this );
        this.onPaymentAdded = this.onPaymentAdded.bind( this );
        this.onAddressChange = this.onAddressChange.bind( this );
        this.onAddressAdded = this.onAddressAdded.bind( this );
        this.handleSubmitPayment = this.handleSubmitPayment.bind( this );

        this.setState( {
            paymentMethodDefault: null,
            addressBook: props.addressBook,
            paymentMethods: props.paymentMethods
        } );
    }

    componentWillMount() {
        const {account} = this.props;
        this.setState( {
            requiresPO: ['LGP', 'UNIV', 'GOV'].includes( account.salesChannel )
        } );
    }

    componentDidMount() {
        // fetch current paymentSelection from localStore to initialize pickers
        CustomerService.getSelectedPaymentOption().then( ( {data} ) => {
            const {paymentMethods, addressBook} = this.state;
            const {paymentId, billingAddressId, PONumber} = data.selectedPayment;

            this.setState( {PONumber: PONumber || this.props.poNumber} );

            if ( paymentId ) {
                const selectedPaymentOption = paymentMethods.find( ( {id} ) => id === paymentId );
                this.setState( {selectedPaymentOption} );
            }
            if ( billingAddressId ) {
                const selectedAddressOption = addressBook.find( ( {id} ) => id === billingAddressId );
                this.setState( {selectedAddressOption} );
            }

        } );
    }

    scrollToTop() {
        var pane = document.querySelector( $CONTENT_WINDOW );
        if ( pane ) {
            pane.scrollTop = 0;
        }
        window.scrollTop = 0;
    }

    onPONumberChange( PONumber ) {
        CustomerService.setSelectedPaymentOption( {PONumber} );
        this.setState( {PONumber} );
    }

    onPaymentChange( payment ) {
        console.log( '[PaymentSelection]:onPaymentChange', payment );
        this.setPaymentOption( payment );
    }

    onAddressChange( address ) {
        console.log( '[PaymentSelection]:onAddressChange', address );
        this.setAddressOption( address );
    }

    onPaymentAdded( addedPayment ) {
        console.log( '[PaymentSelection]:onPaymentAdded', addedPayment );
        const normalizedPayment = User.Wallet.normalizeCreditCard( addedPayment );
        this.setState( {paymentMethods: this.state.paymentMethods.concat( [normalizedPayment] )} );
        this.setPaymentOption( normalizedPayment );
        this.scrollToTop();
    }

    onAddressAdded( addedAddress ) {
        console.log( '[PaymentSelection]:onAddressAdded', addedAddress );
        this.setAddressOption( addedAddress );
        this.setState( {addressBook: this.state.addressBook.concat( [addedAddress] )} );
    }

    setPaymentOption( payment ) {
        CustomerService.setSelectedPaymentOption( {paymentId: payment.id} );
        this.setState( {selectedPaymentOption: payment} );
    }

    setAddressOption( address ) {
        CustomerService.setSelectedPaymentOption( {billingAddressId: address.id} );
        this.setState( {selectedAddressOption: address} );
    }

    handleSubmitPayment( values ) {
        /* note -> we send reference to the submit button due to a legacy implementation in checkout.js
         * the legacy Cart/Checkout module inspects the current label of the button which submitted the form
         * to determine if the "processing order" layer should be displayed
         */
        invoke( this.props, 'onSubmitPayment', {target: this.submitButton.base} );
    }

    render() {
        const {addressBook, paymentMethods, PONumber, selectedAddressOption, selectedPaymentOption, requiresPO} = this.state;
        const addressDefault = selectedAddressOption || User.Address.getSuggestedBillingAddress( addressBook );
        const paymentMethodDefault = selectedPaymentOption || User.Wallet.getDefaultPaymentMethod( paymentMethods );

        const initialValues = {
            PONumber,
            SelectedPaymentMethod: prop( 'id', selectedPaymentOption ),
            SelectedBillingAddress: prop( 'id', selectedAddressOption )
        };

        // Add new credit card form
        if ( !paymentMethods.length ) {
            return (
                <AddPaymentForm
                    addressBook={addressBook}
                    onPaymentAdded={this.onPaymentAdded}/>
            );
        }

        // Select from existing payment methods
        return (
            <div>
                <div className="my-8">
                    <Wallet
                        id="wallet"
                        className="my-4"
                        groupId="SelectedPaymentMethod"
                        paymentMethods={paymentMethods}
                        addressBook={addressBook}
                        defaultPaymentMethod={paymentMethodDefault}
                        allowAddItem={true}
                        onPaymentChange={this.onPaymentChange}
                        onPaymentAdded={this.onPaymentAdded}
                        onAddressAdded={this.onAddressAdded}
                    />
                </div>
                {prop( 'paymentMethodType', selectedPaymentOption ) === 'terms' &&
                <div className="my-8">
                    <AddressBook
                        id="addressbook"
                        label="Billing Address"
                        groupId="SelectedBillingAddress"
                        addresses={addressBook}
                        defaultAddress={addressDefault}
                        onAddressChange={this.onAddressChange}/>
                </div>
                }
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validate={formData => {
                        const errors = {};
                        const trimmedFormData = trimFormData( formData );
                        const {SelectedPaymentMethod, SelectedBillingAddress, PONumber} = trimmedFormData;

                        if ( requiresPO ) {
                            errors.PONumber = !PONumber && {
                                short: 'This field is required',
                                long: 'Purchase order number is required'
                            };
                        }

                        errors.SelectedPaymentMethod =
                            !SelectedPaymentMethod && 'Please select or add a credit card';

                        if ( SelectedPaymentMethod && selectedPaymentOption.paymentMethodType === 'terms' ) {
                            errors.SelectedBillingAddress =
                                !SelectedBillingAddress && 'Please select or add a billing address';
                        }

                        return reject( not, errors );
                    }}
                    onSubmit={values => this.handleSubmitPayment( trimFormData( values ) )}
                    render={formikBag =>
                        <div className="my-12">
                            <TextField
                                label={`Purchase order number ${requiresPO ? '(required)' : '(optional)'}`}
                                id="PONumber"
                                type="text"
                                onChange={e => {
                                    formikBag.handleChange( e );
                                    this.onPONumberChange( e.target.value );
                                }}
                                value={formikBag.values.PONumber}
                                error={formikBag.errors.PONumber}
                                maxLength="50"/>

                            <FormFeedback {...formikBag}/>

                            <div className="my-8 flex justify-center">
                                <Button ref={ref => this.submitButton = ref}
                                        fill
                                        large
                                        type="submit"
                                        onClick={formikBag.handleSubmit}>
                                    Complete payment
                                </Button>
                            </div>
                        </div>
                    }/>
            </div>
        );
    }
}

export default withQuery( PaymentSelection );